// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Partner from "@classes/Partner";
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import ActivityIndicator from "@components/ActivityIndicator/ActivityIndicator";
import Button from "@components/Button/Button";
import CobSectionNumber from "./components/SectionNumber/SectionNumber";
import Form from "@components/Form/Form";
import StackLayout from "@components/layouts/StackLayout/StackLayout";

// HELPERS
import {connectRedux, createClassName, dateUTCtoCET, delay, handleRouteHidden, parseQueryString} from "@helpers/utils";
import api from "../../helpers/api";
import i18n from "@helpers/i18n";
import Settings from "@classes/Settings";

// REDUCERS
import {actions as appActions} from "@reducers/app";
// VIEWS
import SignModal from "./views/SignModal/SignModal";
import SignConfirmationModal from "./views/SignModal/SignConfirmationModal";
import SendToAdvisorModal from "./views/SendToAdvisorModal/SendToAdvisorModal";
import Modal from "../../components/Modal/Modal";
import imgAssently from "@assets/img/ic_assently.png";

const viewsById = {};

export default connectRedux(
    state => ({app: state.app}),
    {
        setAppState: appActions.setState,
        openErrorModal: appActions.openErrorModal
    },
    class CobRoute extends React.PureComponent
    {
        state = _getInitialState.call(this);
        sectionRefs = {};
        startView = null;
        caseStatusTimer = null;
        checkingCaseStatus = false;

        async componentDidMount()
        {
            await handleRouteHidden(this, true);
            const {actions, history, match, location} = this.props;
            const newAppStateProps = {};
            const newStateProps = {busy: false};

            // Setup session and partner
            const session = (newAppStateProps.session = new Session());
            const partner = (newAppStateProps.partner = new Partner(match.params.partnerName));

            // If query params are set in address bar, set context and auth token in session.
            if (location.search)
            {
                const queryParams = parseQueryString(location.search);

                if (queryParams.key)
                {
                    session.setAppKey(queryParams.key);
                }

                if (queryParams.context)
                {
                    /**
                     * GLOF-1017 If the partner name or data contains non-latin1 characters we'll get an error.
                     * A workaround for this is to encode the string before encoding the string...
                     */
                    let b64 = decodeURIComponent(escape(atob(queryParams.context)));
                    session.setContext(JSON.parse(b64));
                }

                if (queryParams.token)
                {
                    session.setAuthToken(queryParams.token);
                }
            }

            /**
             * Available banks in dropdowns for Autogiro
             */
            try
            {
                newAppStateProps.availableBanks = await _setupAvailableBanksAsync.call(this);
            }
            catch (ignored)
            {
                // this fails if auth token is invalid
            }

            try
            {
                await partner.loadData(session.authToken);

                /**
                 * For localization, translation files found in src/i18n
                 */
                Settings.set("LANGUAGE", partner.language);

                /**
                 * If documentprocessor is Assently we also do the Assently component status call, to see if anything
                 * is currently malfunctioning
                 */
                if (partner.documentProcessor === "assently")
                {
                    console.log("Checking status of Assently components... ");
                    const response = await api("/assently/status");

                    if (response.status !== "OK")
                    {
                        newStateProps.assentlyComponents = response.components;
                    }
                }

                newStateProps.visibleSections = {};

                for (let i = 0; i < partner.sections.length; i++)
                {
                    const section = partner.sections[i];
                    viewsById[section.name] = {
                        id: section.name,
                        title: section.title,
                        View: require("./views/" + section.view).default,
                        params: {}
                    };

                    if (section.params)
                    {
                        viewsById[section.name].params = section.params;
                    }

                    newStateProps.visibleSections[section.name] = section.hasOwnProperty("visible") ? section.visible : true;
                }

                newStateProps.visibleSectionsNumber = this._getVisibleSectionsNumber(newStateProps.visibleSections);
                newStateProps.hideFurtherButton = partner._data.hideFurtherButton;

                /**
                 * Support different start pages - if set to "-" we will skip it and go straight to the form
                 */
                if (partner.startView !== "-")
                {
                    this.startView = require("./views/Start/" + partner.startView).default;
                }

                // Load lookup data using customer's id (ssn/org number) provided in context
                if (session.context)
                {
                    const {id: customerId, check} = session.context.customer;
                    const isPerson = Validator.validateSsn(customerId, true);
                    const isCompany = Validator.validateOrgNum(customerId, true);

                    if (!isPerson && !isCompany)
                    {
                        throw new Error("INVALID_IDENTITY");
                    }

                    await session.loadLookupData({
                        type: isPerson ? "person" : "company",
                        value: customerId
                    });

                    // Set the custormer id and checksum string
                    newStateProps.checksum = check;
                    newStateProps.customerId = customerId;

                    // Tell new upcoming state that lookup data has been loaded, so no need to mount Start view
                    newStateProps.lookupDataLoaded = "true";

                    /**
                     * If we are not a company we need to filter out company specific sections from the partner workflow
                     */
                    if (!session.isCompany)
                    {
                        this._removeNonCompanyViews(partner);
                    }
                    else
                    {
                        // Company session, remove views that are only visible to private individuals
                        this._removePrivateExclusiveViews(partner);
                    }
                }

                // The following views should start out in a fulfilled (green) state as they are all either
                // optional or filled out sufficiently in their initial state.
                newAppStateProps.fulfilledViewsById = [];
                newAppStateProps.fulfilledViewsById["FuturNotes"] = true;
                newAppStateProps.fulfilledViewsById["attachfiles"] = true;
                newAppStateProps.fulfilledViewsById["attachfiles-company"] = true;

                // Views only fulfilled for private individuals
                if (!session.isCompany)
                {
                    newAppStateProps.fulfilledViewsById["FuturInsured"] = true;
                }

                await actions.setAppState(newAppStateProps);

                // Check db if there's any drafts
                const draftCases = await this._getCaseDrafts(newStateProps.customerId, newStateProps.checksum);

                // If we pass a context variable containing customer info and the form requires previous documentation to have
                // been filled out, we do the check - for now we don't limit the customer, but simply set the documentationMissing variable
                // or not
                if (session.context && partner.requireDownloadableDocumentation)
                {
                    const downloadableProtocolInfo = await this._checkHasDownloadableProtocol(newStateProps.customerId, newStateProps.checksum);
                    newStateProps.hasDownloadableDocumentation = downloadableProtocolInfo.filename && downloadableProtocolInfo.filename.length > 0;
                    newStateProps.downloadableDocumentationIsKyc = downloadableProtocolInfo.is_kyc_doc;
                    newStateProps.downloadableDocumentationSignDate = downloadableProtocolInfo.sign_date;
                }

                // Set new state
                await delay(750);

                // Have we passed a draftCaseId that we want to auto-load when the form opens?
                if (session.context &&
                    session.context.draftCaseId !== undefined &&
                    session.context.draftCaseId !== null &&
                    draftCases &&
                    draftCases.length > 0)
                {
                    this.setState({...newStateProps, draftCases}, () => {

                        for (let i = 0; i < draftCases.length; i++)
                        {
                            const draftCaseId = draftCases[i].CaseId.toLowerCase();
                            if (draftCaseId === session.context.draftCaseId.toLowerCase())
                            {
                                const caseData = JSON.parse(draftCases[i].CaseData).data;
                                this._applyDraft(draftCaseId, caseData);
                            }
                        }
                    });
                }
                else // No case id, show the form normally
                {
                    this.setState({...newStateProps, draftCases});
                }

                // Setup scroll listener
                window.addEventListener("scroll", this._onWindowScroll, false);
            }
            catch (e)
            {
                console.error(e.message);

                if (e.message === "REQUEST_TIMED_OUT")
                {
                    await actions.openErrorModal({
                        title: "Ett fel uppstod",
                        body: "Anslutningen tog för lång tid. Vänligen ladda om sidan.",
                        closeButtonLabel: "Ladda om sidan",
                        onCloseButtonClick: () => window.location.reload()
                    });
                }
                else if (e.message === "FRAUD_FLAG")
                {
                    await actions.openErrorModal({
                        title: "Personnummer låst",
                        body: "Kundens personnummer är låst via en extern ID-spärr.",
                        closeButtonLabel: "Gå tillbaka",
                        onCloseButtonClick: () => history.go(-1)
                    });
                }
                else
                {
                    history.replace(`${location.pathname}/error`, {
                        error: {message: e.message}
                    });
                }
            }
        }

        componentWillUnmount()
        {
            handleRouteHidden(this, false);
            window.removeEventListener("scroll", this._onWindowScroll);
        }

        render()
        {
            const {props, state} = this;
            const {app} = props;
            const {
                partner,
                session,
                latestViewStates,
                fulfilledViewsById,
                availableBanks,
                attachedDocuments
            } = app;            

            let isFulfilled = true;
            if (partner)
            {
                for (let i = 0; i < partner.sections.length; i++)
                {
                    const sectionName = partner.sections[i].name;
                    if (!Object.hasOwn(fulfilledViewsById, sectionName))
                    {
                        isFulfilled = false;
                    }
                }
            }
            else
            {
                isFulfilled = false;
            }

            const classes = createClassName("CobRoute", {
                "App-route": true,
                hidden: state.hidden,
                fulfilled: isFulfilled
            });

            if (state.busy)
            {
                return (
                    <div className={classes}>
                        <div className="Cob-loading">
                            <ActivityIndicator />
                            <p>{i18n("general", "please_wait")}</p>
                        </div>
                    </div>
                );
            }

            if (!state.lookupDataLoaded && this.startView != null)
            {
                return (
                    <div className={classes}>
                        <this.startView
                            {...props}
                            assentlyStatus={state.assentlyComponents}
                            onLookupDataLoaded={this._onLookupDataLoaded}
                        />
                    </div>
                );
            }
            
            let formCallToAction = i18n("general", "create_form");
            if (partner.formCta)
            {
                formCallToAction = partner.formCta;
            }
            if (session.context &&
                session.context.partner &&
                session.context.partner.role &&
                session.context.partner.role === "assistant")
            {
                formCallToAction = i18n("general", "notify_advisor");
            }

            let formButtonText = "";
            if (partner.formCtaButton)
            {
                formButtonText = partner.formCtaButton;
            }

            if (partner.formHeaderText)
            {
                if (session.isCompany)
                {
                    partner.setData(
                        "form-header-text",
                        partner.formHeaderText.replace("#accountType#", "depå")
                    );
                }
                else
                {
                    partner.setData(
                        "form-header-text",
                        partner.formHeaderText.replace("#accountType#", "ISK")
                    );
                }
            }

            const partnerViewIds = [];
            for (let i = 0; i < partner.sections.length; i++)
            {
                partnerViewIds.push([partner.sections[i].name]);
            }            

            return (
                <div className={classes}>
                    <Modal className="Cob-SignAgreement-SignModal"
                           visible={state.showFuturDialogSign}
                           title={"Avtalet klart för signering"}>
                        {state.signUrls && state.signUrls.length > 0 && (
                            <React.Fragment>
                                <p>
                                    Kapitalförsäkringsdepån är nu nästan klar. Det som återstår är:
                                </p>
                                <p>
                                    <strong>1)</strong> Att rådgivaren samt kunden signerar
                                    mötesbilagan som dokumenterar rådgivningstillfället. Detta
                                    görs med Bank-ID hos vår signeringstjänst Assently:
                                </p>
                                <ul className={"signing-links"}>
                                    <li>
                                        <a href={state.signUrls[0].PartyUrl} target={"_blank"} rel="noreferrer">
                                            <img src={imgAssently}
                                                 style={{height: "20px", marginRight: "10px"}}
                                                 alt="Ladda ner" />
                                            <div>
                                                Klicka för att läsa och signera mötesbilagan
                                                <span>Öppnas i nytt fönster</span>
                                            </div>
                                        </a>
                                    </li>
                                </ul>
                                <p>
                                    <strong>2)</strong> Att kunden signerar avtalet för depån i
                                    vår signeringsportal hos Futur. Vänligen klicka på länken
                                    nedan för att komma dit. Notera att kunden måste logga in som
                                    privatperson.
                                </p>
                            </React.Fragment>
                        )}

                        {(!state.signUrls || state.signUrls.length === 0) && (
                            <React.Fragment>
                                <p>
                                    Din kapitalförsäkringsdepå är nu nästan klar. Det som återstår
                                    är att kunden signerar avtalet i vår signeringsportal.
                                    Vänligen klicka på länken nedan för att komma dit. Notera att
                                    kunden måste logga in som privatperson.
                                </p>
                            </React.Fragment>
                        )}

                        <StackLayout className="Modal-buttons-container" fullWidth>
                            <Modal.Button label="Till signeringsportalen (Futur)"
                                          appearance="primary"
                                          onClick={this._goToFuturSign.bind(this)}
                                          filled />

                            <Modal.Button label="Avsluta rådgivningen"
                                          appearance="primary"
                                          onClick={() => window.location = partner.appUrl}
                                          filled />
                        </StackLayout>
                    </Modal>

                    <Modal className="Cob-SignAgreement-SignModal"
                           visible={state.showFuturDialogManual}
                           title={"Avtalet kräver manuell hantering"}>
                        <p>
                            Din kapitalförsäkringsdepå är nu nästan klar men viss information
                            måste kontrolleras manuellt av Futur. Detta kan ta upp till två
                            bankdagar. Vi återkommer till dig (kunden) när det är dags att
                            signera.
                        </p>

                        <Modal.ButtonsContainer>
                            <Modal.Button label="Avsluta rådgivningen"
                                          appearance="primary"
                                          onClick={() => window.location = partner.appUrl}
                                          filled />
                        </Modal.ButtonsContainer>
                    </Modal>
                    <React.Fragment>
                        <StackLayout orientation="vertical" alignItems="start" fullWidth>
                            <Form id="cobForm" onSubmit={this._onFormSubmit} submitOnEnter={false}>
                                {state.caseId && (
                                    <input
                                        type="hidden"
                                        name="caseId"
                                        value={state.caseId}
                                    />
                                )}
                                {session.context && session.context.partner && (
                                    <div style={{display: "none"}}>
                                        {session.context.partner.ssn && (
                                            <input
                                                type="hidden"
                                                name="partner_ssn"
                                                value={session.context.partner.ssn}
                                            />
                                        )}

                                        {session.context.partner.firstName && (
                                            <input
                                                type="hidden"
                                                name="partner_firstname"
                                                value={session.context.partner.firstName}
                                            />
                                        )}

                                        {session.context.partner.email && (
                                            <input
                                                type="hidden"
                                                name="partner_email"
                                                value={session.context.partner.email}
                                            />
                                        )}

                                        {session.context.partner.lastName && (
                                            <input
                                                type="hidden"
                                                name="partner_lastname"
                                                value={session.context.partner.lastName}
                                            />
                                        )}

                                        {session.context.partner.firstName && session.context.partner.lastName && (
                                            <input
                                                type="hidden"
                                                name="advisor_name"
                                                value={session.context.partner.firstName + " " + session.context.partner.lastName}
                                            />
                                        )}


                                        {session.context.partner.companyName && (
                                            <input
                                                type="hidden"
                                                name="partner_company"
                                                value={session.context.partner.companyName}
                                            />
                                        )}

                                        {session.context.partner.customerNumber && (
                                            <input
                                                type="hidden"
                                                name="partner_customer_number"
                                                value={session.context.partner.customerNumber}
                                            />
                                        )}

                                        {session.context.partner.street && (
                                            <input
                                                type="hidden"
                                                name="partner_address_street"
                                                value={session.context.partner.street}
                                            />
                                        )}

                                        {session.context.partner.zipCode && (
                                            <input
                                                type="hidden"
                                                name="partner_address_zipcode"
                                                value={session.context.partner.zipCode}
                                            />
                                        )}

                                        {session.context.partner.city && (
                                            <input
                                                type="hidden"
                                                name="partner_address_city"
                                                value={session.context.partner.city}
                                            />
                                        )}

                                        {session.context.partner.phoneNumber && (
                                            <input
                                                type="hidden"
                                                name="partner_phonenumber"
                                                value={session.context.partner.phoneNumber}
                                            />
                                        )}

                                        {session.context.partner.companyName && (
                                            <input
                                                type="hidden"
                                                name="company_name"
                                                value={session.context.partner.companyName}
                                            />
                                        )}
                                    </div>
                                )}

                                {partner.formHeaderText && (
                                    <p
                                        style={{
                                            margin: "0 auto 10px auto",
                                            fontSize: "16px",
                                            maxWidth: "520px",
                                            textAlign: "center",
                                            fontWeight: "300"
                                        }}
                                        dangerouslySetInnerHTML={{__html: partner.formHeaderText}}
                                    />
                                )}

                                {partner.formLongHeader && partner.formLongText && (
                                    <div style={{width: "100%", display: "block"}}>
                                        <h1
                                            style={{
                                                width: "100%",
                                                textAlign: "center",
                                                fontWeight: "bold"
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: partner.formLongHeader
                                            }}
                                        />

                                        <p style={{
                                            margin: "0 auto 10px auto",
                                            fontSize: "15px",
                                            maxWidth: "520px",
                                            textAlign: "center",
                                            fontWeight: "300"
                                        }}
                                           dangerouslySetInnerHTML={{__html: partner.formLongText}}
                                        />
                                    </div>
                                )}

                                {partner._data.saveDraftButton &&
                                    <div className="Cob-Draft-Cases">
                                        {state.draftCases && state.draftCases.length > 0 && (
                                            <div className="Cob-Draft-Cases-Content">
                                                <h4>Det finns sparade utkast för denna kund:</h4>
                                                {state.draftCases.map((draftCase, index) =>
                                                {
                                                    const caseData = JSON.parse(draftCase.CaseData).data;
                                                    let date = draftCase.LastModifiedDate ? dateUTCtoCET(draftCase.LastModifiedDate) :
                                                        dateUTCtoCET(draftCase.CreatedDate);
                                                    const customerTag = caseData.customer_name || caseData.customer_ssn;

                                                    return (
                                                        <div key={index} className="Cob-Draft-Case">
                                                            <a className="Cob-Draft-Case-select-btn" onClick={() => this._applyDraft(draftCase.CaseId, caseData)}>
                                                                {customerTag} - {date.toLocaleString("sv-SE")} (klicka för att ladda)
                                                            </a>
                                                            <a className="Cob-Draft-Case-remove-btn"
                                                               onClick={() => this._removeDraft(draftCase.CaseId)}>
                                                                Ta bort utkast
                                                            </a>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {state.selectedDraftCase && (
                                            <div className="Cob-Draft-Cases-Content">
                                                <div className="warning">
                                                    Utkastet laddades.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                }

                                <div className="Cob-sections">
                                    {partnerViewIds.map((viewIds, index) => (
                                        <CobSection
                                            app={app}
                                            actions={this.props.actions}
                                            ref={ref => (this.sectionRefs[index + 1] = ref)}
                                            key={index}
                                            viewsById={viewsById}
                                            viewIds={viewIds}
                                            availableBanks={availableBanks}
                                            assentlyStatus={state.assentlyComponents}
                                            hasDownloadableDocumentation={state.hasDownloadableDocumentation}
                                            downloadableDocumentationIsKyc={state.downloadableDocumentationIsKyc}
                                            downloadableDocumentationSignDate={state.downloadableDocumentationSignDate}
                                            index={index}
                                            stickyNumber={
                                                state.stickySectionNumbers[index + 1] || false
                                            }
                                            fulfilledSectionsByNumber={this._mapFulfilledSections()}
                                            latestViewStates={latestViewStates}
                                            partner={partner}
                                            session={session}
                                            onViewChange={this._onViewChange}
                                            visibleSectionsNumber={this.state.visibleSectionsNumber}
                                            toggleVisibleSection={this._toggleVisibleSection}
                                            isFurtherButtonHidden={this.state.hideFurtherButton}
                                            onParentCaseLoaded={this._onParentCaseLoaded}
                                            onManualSignatoriesSet={this._onManualSignatoriesSet}
                                            visible={
                                                this.state.visibleSections[partner.sections[index].name]
                                            }
                                            draftData={this.state.selectedDraftCase}
                                        />
                                    ))}
                                </div>

                                {state.draftSaved && (
                                    <div className="Cob-Draft-Cases">
                                        <div className="Cob-Draft-Cases-Content">
                                            <div className="warning saved">
                                                {
                                                    attachedDocuments && attachedDocuments > 0 ?
                                                        "Utkastet sparades. Notera att bifogade dokument kommer att behöva laddas upp på nytt om avtalet skickas till kund vid senare tillfälle."
                                                        :
                                                        "Utkastet sparades."
                                                }
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {state.sentToAdvisor && (
                                    <div className="Cob-Draft-Cases">
                                        <div className="Cob-Draft-Cases-Content">
                                            <div className="warning saved">
                                                Utkastet skickades till rådgivaren.
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {!state.hideFurtherButton && (
                                    <div className="Cob-buttons">
                                        {partner._data.saveDraftButton &&
                                            <Button
                                                name="save-draft-button"
                                                className="Cob-save-button"
                                                type="submit"
                                                label="Spara utkast "
                                                appearance="success"
                                                disabled={false}
                                                busy={state.draftSaving}
                                            />
                                        }
                                        <Button
                                            type="submit"
                                            label={formCallToAction}
                                            appearance={isFulfilled ? "success" : "medium"}
                                            disabled={!isFulfilled}
                                            busy={state.showSignDialog || state.showSendToAdvisorDialog || state.signing}
                                        />
                                    </div>
                                )}

                                {partner.formSubmitExplanation1 && (
                                    <div style={{
                                        marginTop: "15px",
                                        marginBottom: "0",
                                        fontSize: "15px",
                                        fontStyle: "italic",
                                        maxWidth: "630px",
                                        textAlign: "center",
                                        fontWeight: "300"
                                    }}
                                    >
                                        <p style={{
                                            marginBottom: "5px"
                                        }}
                                        >
                                            {partner.formSubmitExplanation1}
                                        </p>
                                        <p style={{
                                            marginTop: "0",
                                            marginBottom: "20px"
                                        }}
                                        >
                                            {partner.formSubmitExplanation2}
                                        </p>
                                    </div>
                                )}
                            </Form>
                        </StackLayout>

                        <SignModal
                            visible={state.showSignDialog}
                            signatories={state.signatories}
                            signOnScreenEnabled={partner.signOnScreenEnabled}
                            title={formCallToAction}
                            buttonText={formButtonText}
                            onCloseModal={this._closeSignModal.bind(this)}
                            onSign={this._onSignViaModal.bind(this)}
                            onSignOnScreenChange={this._onSignOnScreenChange.bind(this)}
                            onSignatoryCheckedChanged={this._onSignatoryCheckedChanged.bind(
                                this
                            )}
                            signOnScreenChecked={state.signOnScreen}
                            isSigning={state.signing}
                        />
                        <SendToAdvisorModal
                            visible={state.showSendToAdvisorDialog}
                            title={formCallToAction}
                            advisor={(session.context && session.context.partner) ? session.context.partner : null}
                            onCloseModal={this._closeSendToAdvisorModal.bind(this)}
                            onSend={this._onSendToAdvisor.bind(this)}
                            isSigning={state.signing}
                        />

                        <SignConfirmationModal
                            visible={state.sentForSignature}
                            signatories={state.signatories}
                            onCloseModal={this._closeAndResetForm.bind(this)}
                            signUrls={state.signUrls}
                            signOnNewTab={partner.signOnNewTab}
                        />
                    </React.Fragment>
                </div>
            );
        }

        _getVisibleSectionsNumber(visibleSections)
        {
            let count = 0;
            for (const property in visibleSections)
            {
                const value = visibleSections[property];
                if (value)
                {
                    count++;
                }
            }
            return count;
        }

        _goToFuturSign = () =>
        {
            window.open("https://eservice.futurpension.se/selfservice", "_blank");
        };

        // Internal methods
        _onLookupDataLoaded = () =>
        {
            if (!this.props.app.session.isCompany)
            {
                // For non-company sessions we need to remove non-company views
                this._removeNonCompanyViews(this.props.app.partner);
            }
            else
            {
                // Company session, remove views that are only visible to private individuals
                this._removePrivateExclusiveViews(this.props.app.partner);
            }

            this.setState({lookupDataLoaded: true});
        };

        _removeNonCompanyViews(partner)
        {
            let filteredSections = [];
            partner.sections.forEach(function(section)
            {
                const view = viewsById[section.name];
                if (!view.View.companyExclusive)
                {
                    filteredSections[filteredSections.length] = section;
                }
                else
                {
                    console.log(
                        "Skipping section '" +  section.name + "' since it is company exclusive"
                    );
                }
            });
            partner._data.sections = filteredSections;
        }

        _removePrivateExclusiveViews(partner)
        {
            let filteredSections = [];
            partner.sections.forEach(function(section)
            {
                const view = viewsById[section.name];
                if (!view.View.privateExclusive)
                {
                    filteredSections[filteredSections.length] = section;
                }
                else
                {
                    console.log("Skipping section '" + section.name + "' since it is exclusive for private individuals");
                }
            });
            partner._data.sections = filteredSections;
        }

        _onViewChange = (id, viewState, isFulfilled) =>
        {
            const {app} = this.props;

            const fulfilledViewsById = {...app.fulfilledViewsById};
            if (isFulfilled)
            {
                fulfilledViewsById[id] = true;
            }
            else
            {
                if (typeof fulfilledViewsById[id] !== "undefined")
                {
                    delete fulfilledViewsById[id];
                }
            }

            this.props.actions.setAppState({
                latestViewStates: {...app.latestViewStates, [id]: viewState},
                fulfilledViewsById
            });
        };

        _promisedSetState = newState =>
            new Promise(resolve => this.setState(newState, resolve));

        _toggleVisibleSection = async (sectionName, setVisible) =>
        {
            // This function should only be called from swish configurations or risk unwanted behaviour with Further button getting hidden
            if (this.state.visibleSections[sectionName] === setVisible)
            { // if theres' no change in section state return false
                return false;
            }

            const visibleSectionsNumber =
                setVisible ?
                    this.state.visibleSectionsNumber + 1
                    : this.state.visibleSectionsNumber > 1 ?
                        this.state.visibleSectionsNumber - 1
                        : 1;

            new Promise(resolve =>
                this.setState(
                    {
                        ...this.state,
                        hideFurtherButton: visibleSectionsNumber <= 2 ? true : false,
                        visibleSections: {
                            ...this.state.visibleSections,
                            [sectionName]: setVisible
                        },
                        visibleSectionsNumber
                    },
                    resolve
                )
            );
        };

        _onWindowScroll = () =>
        {
            const sections = Object.keys(this.sectionRefs)
                .sort((a, b) => (a > b ? 1 : -1))
                .map(n => ({
                    number: n,
                    numberElement: this.sectionRefs[n].numberRef.containerElement
                }));
            const stickySectionNumbers = {};

            for (let i = 0, n = sections.length; i < n; i++)
            {
                const section = sections[i];
                const {number, numberElement} = section;
                const index = number - 1;
                const rect = numberElement.getBoundingClientRect();
                stickySectionNumbers[number] = rect.top < 20 * number + 40 * index;
            }

            this.setState({stickySectionNumbers});
        };

        /**
         * When the user clicks the save button we save the form
         * When the user clicks the sign button we open the SignModal dialog where more options are presented.
         */
        _onFormSubmit = data =>
        {
            const activeElement = document.activeElement;
            const isSaveDraftClick = (activeElement.name === "save-draft-button");

            const {session} = this.props.app;
            session.updateData(data);

            if (isSaveDraftClick)
            {
                return this._onSaveDraft(data);
            }

            let signatories = this._getSignatoriesFromAppSession(session);

            if (session.context &&
                session.context.partner &&
                session.context.partner.role &&
                session.context.partner.role === "assistant")
            {
                this._onSaveDraft(data);
                return this.setState({showSendToAdvisorDialog: true, signatories: signatories});
            }


            if (this.state.manualSignatories.length > 0)
            {
                signatories = this.state.manualSignatories;
            }

            if (signatories.length > 0)
            {
                this.setState({showSignDialog: true, signatories: signatories});
            }
            else
            {
                this.setState(
                    {showSignDialog: false, signatories: signatories},
                    () =>
                    {
                        this._onSignViaModal();
                    }
                );
            }
        };

        _closeSignModal()
        {
            this.setState({showSignDialog: false});
        }

        _closeSendToAdvisorModal()
        {
            this.setState({showSendToAdvisorDialog: false});
        }

        _closeAndResetForm()
        {
            const {partner} = this.props.app;
            this.setState({showSignDialog: false, sentForSignature: false});

            // If partner has a redirect link, we send them there after pressing the close button
            if (partner.redirectUrl !== null && partner.redirectUrl !== "")
            {
                window.location = partner.redirectUrl;
            }
        }

        _onSignOnScreenChange()
        {
            this.setState({signOnScreen: !this.state.signOnScreen});
        }

        _onSignatoryCheckedChanged(val, index)
        {
            const newSignatories = JSON.parse(JSON.stringify(this.state.signatories));
            newSignatories[index].selected = !newSignatories[index].selected;
            this.setState({signatories: newSignatories});
        }

        async _checkCaseStatus(caseData)
        {
            if (this.checkingCaseStatus)
            {
                return;
            }

            const caseId = caseData.caseId;

            console.log("Checking case status for case with ID " + caseId);
            this.checkingCaseStatus = true;

            const response = await api("/case/status", {caseId: caseId});
            this.checkingCaseStatus = false;

            if (response.status === "pending" || response.status === "received")
            {
                console.log("Case status is pending...");
                return;
            }

            /**
             * Status is not pending, no need to rerun the status check
             */
            if (this.caseStatusTimer != null)
            {
                clearInterval(this.caseStatusTimer);
                this.caseStatusTimer = null;
            }

            if (response.status === "rejected")
            {
                this.setState(
                    {
                        signing: false,
                        showSignDialog: false,
                        showFuturDialogManual: false,
                        showFuturDialogSign: false,
                        signUrls: caseData.signatories
                    },
                    async () =>
                    {
                        await this.props.actions.openErrorModal({
                            title: "Ett fel uppstod",
                            body: response.msg
                        });
                    }
                );
            }
            else if (response.status === "waitforsign")
            {
                await api("/case/notify", {
                    caseId: caseId
                });

                this.setState({
                    signing: false,
                    showSignDialog: false,
                    showFuturDialogManual: false,
                    showFuturDialogSign: true,
                    signUrls: caseData.signatories
                });
            }
            else if (response.status === "pendingmanualvalidation")
            {
                this.setState({
                    signing: false,
                    showSignDialog: false,
                    showFuturDialogManual: true,
                    showFuturDialogSign: false,
                    signUrls: caseData.signatories
                });
            }
            else
            {
                console.log("Unknown response code", response);
                this.setState({signing: false, showSignDialog: false}, async () =>
                {
                    await this.props.actions.openErrorModal({
                        title: "Ett fel uppstod",
                        body: "Var god kontakta backoffice."
                    });
                });
            }
        }

        _onSendToAdvisor()
        {
            const {props} = this;
            const {actions, app} = props;
            const { session } = app;

            this.setState({signing: true}, async () =>
            {
                try
                {
                  const data = {
                    // caseSessionURL: window.location.href,
                    assistant: session.context.partner,
                    customerId: session.context.customer.id,
                  }

                  // Send email notification to advisor
                  await session.sendToAdvisor(data);

                  this.setState({
                    showSendToAdvisorDialog: false,
                    draftSaved: false,
                    sentToAdvisor: true,
                    selectedDraftCase: null,
                    signing: false
                  });
                }
                catch (e)
                {
                    console.error(e);
                    this.setState({signing: false, showSendToAdvisorDialog: false}, async () =>
                    {
                        if (e.message === "rejected")
                        {
                            let errorBody = "Ansökan kunde inte tas emot. Vänligen kontakta backoffice.";

                            if (e.details)
                            {
                                errorBody = "Ansökan kunde inte tas emot. Svar från Futur: ";
                                for (let i = 0; i < e.details.length; i++)
                                {
                                    errorBody += e.details[i] + " ";
                                }
                            }

                            await actions.openErrorModal({
                                title: "Ett fel uppstod",
                                body: errorBody
                            });
                        }
                        else if (e.message === "REQUEST_TIMED_OUT")
                        {
                            await actions.openErrorModal({
                                title: "Ett fel uppstod",
                                body: "Anropet tog för lång tid. Vänligen försök igen."
                            });
                        }
                        else if (e.message === "UPLOAD_FAILED")
                        {
                            await actions.openErrorModal({
                                title: "Filuppladdning misslyckades",
                                body: "Filerna kunde inte laddas upp. Prova igen eller ta bort bifogade filer för att fortsätta."
                            });
                        }
                    });
                }
            });
        }

        _onSignViaModal()
        {
            const {props} = this;
            const {actions, app} = props;
            const {partner, session} = app;

            let caseId = null;

            this.setState({signing: true}, async () =>
            {
                try
                {
                    // Upload to Assently
                    const caseData = await session.upload(
                        partner.name,
                        this.state.signOnScreen,
                        this.state.signatories
                    );
                    caseId = caseData.caseId;
                    console.log("New case ID: ", caseId);

                    if (caseData.documentProcessor === "futur")
                    {
                        /**
                         * Check the status of the application until we get a response other than pending:
                         * if in need of manual approval show a message
                         * if it is ready for sign and show a confirmation message with a link to the signing portal
                         */
                        this.caseStatusTimer = setInterval(
                            () => this._checkCaseStatus(caseData),
                            3000
                        );
                        await this._checkCaseStatus(caseData);
                    }
                    else
                    {
                        /**
                         * skipSelectionOfSingleSignatory
                         *
                         * if set to true and there is only one signatory assigned to the case we redirect to the agreement and
                         * page where everything can be signed.
                         */
                        if (caseData.signatories &&
                            caseData.signatories.length === 1 &&
                            partner.skipSelectionOfSingleSignatory)
                        {
                            window.location = caseData.signatories[0].PartyUrl;
                        }
                        else if (partner.skipSignConfirmationPage)
                        {
                            window.location = partner.redirectUrl + "?redirect=" + partner.name;
                        }
                        else
                        {
                            this.setState({
                                showSignDialog: false,
                                signing: false,
                                sentForSignature: true,
                                signUrls: caseData.signatories
                            });
                        }
                    }
                }
                catch (e)
                {
                    console.error(e.message);
                    this.setState({signing: false, showSignDialog: false}, async () =>
                    {
                        if (e.message === "rejected")
                        {
                            let errorBody = "Ansökan kunde inte tas emot. Vänligen kontakta backoffice.";

                            if (e.details)
                            {
                                errorBody = "Ansökan kunde inte tas emot. Svar från Futur: ";
                                for (let i = 0; i < e.details.length; i++)
                                {
                                    errorBody += e.details[i] + " ";
                                }
                            }

                            await actions.openErrorModal({
                                title: "Ett fel uppstod",
                                body: errorBody
                            });
                        }
                        else if (e.message === "REQUEST_TIMED_OUT")
                        {
                            await actions.openErrorModal({
                                title: "Ett fel uppstod",
                                body: "Anropet tog för lång tid. Vänligen försök igen."
                            });
                        }
                        else if (e.message === "UPLOAD_FAILED")
                        {
                            await actions.openErrorModal({
                                title: "Filuppladdning misslyckades",
                                body: "Filerna kunde inte laddas upp. Prova igen eller ta bort bifogade filer för att fortsätta."
                            });
                        }
                    });
                }
            });
        }

        _applyDraft = (caseId, caseData) =>
        {
            this.setState({caseId, draftCases: [], selectedDraftCase: caseData}, async () =>
            {
                // Add half second delay to allow the form to be rendered
                await delay(500);

                const form = document.getElementById("cobForm");
                // Iterate over each field in the form
                for (let i = 0; i < form.elements.length; i++)
                {
                    const field = form.elements[i];
                    // Trigger the onBlur event for the field (Validate)
                    field.dispatchEvent(new Event("blur", {bubbles: true}));
                }

            });
        };

        _removeDraft = async (caseId) =>
        {
            const {state} = this;
            const {customerId, checksum} = state;

            if (!window.confirm("Är du säker på att du vill ta bort utkastet?")) {
                return;
            }

            try
            {
                await api("/case/delete", {identityNo: customerId, checksum, caseId});

                // remove element from state draftCases
                const draftCases = state.draftCases.filter(function(element)
                {
                    return element["CaseId"] !== caseId;
                });
                this.setState({draftCases});
            }
            catch (e)
            {
                console.error(e.message);
            }
        };

        async _onSaveDraft(data)
        {
            const {state} = this;
            const {customerId, checksum} = state;

            this.setState({draftSaving: true});

            try
            {
                // Save to DB
                const response = await api("/case/save", {
                    identityNo: customerId,
                    checksum,
                    formFields: data
                    // documents: this.documents
                });

                const caseId = response.caseId;
                this.setState({draftSaving: false, draftSaved: true, caseId});
                console.log("Saved draft with case ID: ", caseId);
            }
            catch (e)
            {
                console.error(e.message);
            }
        }

        async _checkHasDownloadableProtocol(customerId, checksum)
        {
            try
            {
                return await api("/case/download/latest", {identityNo: customerId, checksum: checksum, check_exists: 1});

            }
            catch (exception)
            {
                return false;
            }
        }

        async _getCaseDrafts(customerId, checksum)
        {
            try
            {
                const {props} = this;
                if (!props.app.session.context || !props.app.session.context.partner)
                {
                    return null;
                }
                const partnerSsn = props.app.session.context.partner.ssn;
                return await api("/partner/drafts", {identityNo: customerId, checksum, partnerSsn});
            }
            catch (e)
            {
                if (e !== undefined)
                {
                    console.error(e);
                }
            }
        }

        _onParentCaseLoaded = (caseData) =>
        {
            this.setState({parentCaseData: caseData});
        };

        _onManualSignatoriesSet = (signatories) =>
        {
            this.setState({manualSignatories: signatories});
        };

        _mapFulfilledSections = () =>
        {
            const {app} = this.props;
            const {partner, fulfilledViewsById} = app;
            const {sections} = partner;
            // console.log(sections);
            const result = {};

            for (let i = 0, n = sections.length; i < n; i++)
            {
                const viewIds = sections[i];
                if (fulfilledViewsById[viewIds.name] === true)
                {
                    result[i + 1] = true;
                }
            }

            return result;
        };

        _getSignatoriesFromAppSession(appSession)
        {
            const signatories = [];

            let isCompanySession;
            if (appSession.lookupData !== null)
            {
                isCompanySession = appSession.isCompany;
            }
            else
            {
                isCompanySession =
                    !Validator.validateSsn(
                        appSession.data["customer_social_security_number"],
                        true
                    ) &&
                    Validator.validateOrgNum(
                        appSession.data["customer_social_security_number"],
                        true
                    );
            }

            if (isCompanySession)
            {
                for (let i = 0; i <= 5; i++)
                {
                    if (appSession.data["checkbox_signatory_" + i] === "yes")
                    {
                        signatories.push({
                            firstName: appSession.data["company_representative_firstname_" + i],
                            lastName: appSession.data["company_representative_lastname_" + i],
                            ssn: appSession.data["company_representative_sec_num_" + i],
                            email: appSession.data["company_representative_email_" + i],
                            phone: "",
                            selected: true
                        });
                    }
                    else if (appSession.data["company_rep_type_signatory_" + i] === "yes")
                    {
                        // Check other format of company signatories
                        signatories.push({
                            firstName: appSession.data["company_rep_first_name_" + i],
                            lastName: appSession.data["company_rep_last_name_" + i],
                            ssn: appSession.data["company_rep_ssn_" + i],
                            email: appSession.data["company_rep_email_" + i],
                            phone: "",
                            selected: true
                        });
                    }
                    else if (this.state.parentCaseData["checkbox_signatory_" + i] && this.state.parentCaseData["checkbox_signatory_" + i] === "yes")
                    {
                        signatories.push({
                            firstName: this.state.parentCaseData["company_representative_firstname_" + i],
                            lastName: this.state.parentCaseData["company_representative_lastname_" + i],
                            ssn: this.state.parentCaseData["company_representative_sec_num_" + i],
                            email: this.state.parentCaseData["company_representative_email_" + i],
                            phone: this.state.parentCaseData["company_representative_phone_" + i],
                            selected: true
                        });
                    }
                }

                return signatories;
            }

            // Check if this is this is a minor and guardians need to sign
            const guardian1name = appSession.data.caretaker_name_1 || "";
            const guardian2name = appSession.data.caretaker_name_2 || "";
            if (guardian1name.length > 0 || guardian2name.length > 0)
            {
                console.log("Minor :: Setting guardians as signatories");
                if (guardian1name.length > 0)
                {
                    const g1firstName =
                        guardian1name.indexOf(" ") > 0
                            ? guardian1name.substring(0, guardian1name.indexOf(" ")).trim()
                            : "-";
                    const g1LasttName =
                        guardian1name.indexOf(" ") > 0
                            ? guardian1name.substring(guardian1name.indexOf(" ")).trim()
                            : guardian1name;
                    const g1Ssn = appSession.data.caretaker_ssn_1;
                    signatories.push(
                        Object.freeze({
                            firstName: g1firstName,
                            lastName: g1LasttName,
                            ssn: g1Ssn,
                            email: appSession.data.customer_email,
                            phone: appSession.data.customer_phonenumber_cell,
                            selected: true
                        })
                    );
                }

                if (guardian2name.length > 0)
                {
                    const g2firstName =
                        guardian2name.indexOf(" ") > 0
                            ? guardian2name.substring(0, guardian2name.indexOf(" ")).trim()
                            : "-";
                    const g2LasttName =
                        guardian2name.indexOf(" ") > 0
                            ? guardian2name.substring(guardian2name.indexOf(" ")).trim()
                            : guardian2name;
                    const g2ssn = appSession.data.caretaker_ssn_2;
                    signatories.push(
                        Object.freeze({
                            firstName: g2firstName,
                            lastName: g2LasttName,
                            ssn: g2ssn,
                            email: appSession.data.customer_email,
                            phone: appSession.data.customer_phonenumber_cell,
                            selected: true
                        })
                    );
                }

                return signatories;
            }

            const signatory = {
                firstName: appSession.data.customer_firstname,
                lastName: appSession.data.customer_lastname,
                ssn: appSession.data.customer_social_security_number,
                email: appSession.data.customer_email,
                phone: appSession.data.customer_phonenumber_cell,
                selected: true
            };
            signatories.push(signatory);

            return signatories;
        }
    }
);

function _setupAvailableBanksAsync()
{
    return new Promise(async (resolve, reject) =>
    {
        try
        {
            console.log("Fetching Available Banks...");
            const result = await api("/banks", {
                formFields: this._data
            });
            resolve(result);
        }
        catch (e)
        {
            console.error(e.message);
            reject();
        }
    });
}

// PRIVATE COMPONENTS
class CobSection extends React.PureComponent
{
    static propTypes = {
        viewsById: PropTypes.object.isRequired,
        viewIds: PropTypes.array.isRequired,
        index: PropTypes.number.isRequired,
        stickyNumber: PropTypes.bool.isRequired,
        fulfilledSectionsByNumber: PropTypes.object.isRequired,
        latestViewStates: PropTypes.object.isRequired,
        partner: PropTypes.instanceOf(Partner).isRequired,
        session: PropTypes.instanceOf(Session).isRequired,
        onViewChange: PropTypes.func.isRequired,
        toggleVisibleSection: PropTypes.func.isRequired,
        visible: PropTypes.bool.isRequired,
        draftData: PropTypes.object
    };

    state = {
        views: _mapSectionViews.call(this)
    };

    render()
    {
        const {props, state} = this;
        const {
            index,
            stickyNumber,
            fulfilledSectionsByNumber,
            latestViewStates,
            partner,
            session,
            onViewChange,
            visibleSectionsNumber,
            toggleVisibleSection,
            availableBanks,
            hasDownloadableDocumentation,
            downloadableDocumentationIsKyc,
            downloadableDocumentationSignDate,
            isFurtherButtonHidden,
            visible,
            onParentCaseLoaded,
            onManualSignatoriesSet,
            draftData
        } = props;

        const sectionNumber = index + 1;
        const isFulfilled = !!fulfilledSectionsByNumber[sectionNumber];

        // limited CobSectionNumber won't have a continuous line leading down as there's no Further button
        const limited = isFurtherButtonHidden && sectionNumber > 1;

        const hideSectionNumbers =
            (partner._data.hideSectionNumbers && visibleSectionsNumber <= 1) || false;

        const classes = createClassName("Cob-Section", {
            hidden: !visible,
            isLast: visibleSectionsNumber === sectionNumber,
            fulfilled: isFulfilled,
            "prev-fulfilled": !!fulfilledSectionsByNumber[sectionNumber - 1],
            "next-fulfilled": !!fulfilledSectionsByNumber[sectionNumber + 1]
        });

        return (
            <div className={classes} id={state.views[0].id}>
                <CobSectionNumber
                    ref={ref => (this.numberRef = ref)}
                    number={sectionNumber}
                    fulfilled={isFulfilled}
                    sticky={stickyNumber}
                    title={"Avsnitt " + (index + 1)}
                    id={state.views[0].id}
                    limited={limited}
                    hidden={hideSectionNumbers}
                />

                <div className={
                    hideSectionNumbers ? "Cob-Views noLeftPadding" : "Cob-views"
                }>
                    {state.views.map(({id, title, View: CobView, params}, i) => (
                        <CobView
                            key={i}
                            id={id}
                            app={props.app}
                            actions={props.actions}
                            title={title}
                            partner={partner}
                            session={session}
                            availableBanks={availableBanks}
                            hasDownloadableDocumentation={hasDownloadableDocumentation}
                            downloadableDocumentationIsKyc={downloadableDocumentationIsKyc}
                            downloadableDocumentationSignDate={downloadableDocumentationSignDate}
                            sectionNumber={sectionNumber}
                            number={i + 1}
                            prevState={latestViewStates[id]}
                            onChange={onViewChange}
                            onParentCaseLoaded={onParentCaseLoaded}
                            onManualSignatoriesSet={onManualSignatoriesSet}
                            toggleVisibleSection={toggleVisibleSection}
                            config={params}
                            draftData={draftData}
                        />
                    ))}
                </div>
            </div>
        );
    }
}

// PRIVATE FUNCTIONS
function _getInitialState()
{
    return {
        hidden: true,
        busy: true,
        hasDownloadableDocumentation: false,
        downloadableDocumentationIsKyc: false,
        downloadableDocumentationSignDate: null,
        checksum: null,
        customerId: null,
        caseId: null,
        assentlyComponents: [],
        lookupDataLoaded: false,
        draftSaving: false,
        draftSaved: false,
        sentToAdvisor: false,
        signing: false,
        sentForSignature: false,
        signUrls: [],
        showSignDialog: false,
        showSendToAdvisorDialog: false,
        showFuturDialogManual: false,
        showFuturDialogSign: false,
        signatories: [],
        manualSignatories: [],
        signOnScreen: false,
        stickySectionNumbers: {},
        parentCaseData: [],
        draftCases: [],
        selectedDraftCase: null
    };
}

function _mapSectionViews()
{
    const {viewsById, viewIds, session} = this.props;
    const views = viewIds.map(id => ({...viewsById[id], id}));
    return views.filter(({View}) =>
    {
        if (View.companyExclusive)
        {
            return session.isCompany;
        }

        if (View.privateExclusive)
        {
            return !session.isCompany;
        }

        return true;
    });
}



// WEBPACK FOOTER //
// ./src/routes/Cob/Cob.js